import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Calendar, Clock, FileText, Globe, Lock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ActivityEdit — minimal edit screen for the four core fields.
 *
 * Matches the visual conventions of ActivityCreate (no redesign).
 * Uses existing backend PUT /api/activities/{activity_id}.
 */
export default function ActivityEdit() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { user, authResolved } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    title: '',
    location: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    visibility: 'public',
  });

  useEffect(() => {
    if (!authResolved) return;
    if (!user) { navigate('/auth?mode=signin'); return; }
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/api/activities/${activityId}`, { headers });
        if (!mounted) return;
        const a = res.data || {};
        const splitIso = (iso) => {
          if (!iso) return ['', ''];
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return ['', ''];
          const pad = (n) => String(n).padStart(2, '0');
          const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
          return [date, time];
        };
        const [sd, st] = splitIso(a.start_time);
        const [ed, et] = splitIso(a.end_time);
        setForm({
          title: a.title || '',
          location: typeof a.location === 'string' ? a.location : (a.location?.name || ''),
          start_date: sd,
          start_time: st,
          end_date: ed,
          end_time: et,
          visibility: a.visibility === 'private' ? 'private' : 'public',
        });
        setLoadError(false);
      } catch {
        setLoadError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activityId, user, authResolved, navigate]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (form.title.length > 200) e.title = 'Title must be under 200 characters';
    if (!form.start_date) e.start_date = 'Start date is required';
    if (!form.start_time) e.start_time = 'Start time is required';
    if (!form.location.trim()) e.location = 'Location is required';
    if (!form.visibility) e.visibility = 'Select visibility';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const startStr = `${form.start_date}T${form.start_time}:00`;
      const payload = {
        title: form.title,
        location: form.location,
        start_time: new Date(startStr).toISOString(),
        visibility: form.visibility,
      };
      if (form.end_date) {
        const endStr = form.end_time
          ? `${form.end_date}T${form.end_time}:00`
          : `${form.end_date}T23:59:00`;
        payload.end_time = new Date(endStr).toISOString();
      } else {
        payload.end_time = null;
      }
      await axios.put(`${API}/api/activities/${activityId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Activity updated');
      navigate(`/activities/${activityId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save changes';
      toast.error(typeof msg === 'string' ? msg : 'Could not save changes');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--fy-text-disabled)' }} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
          <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:opacity-70" aria-label="Back">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <span className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Edit activity</span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8">
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }} data-testid="activity-edit-load-error">
            Couldn't load right now. Try again in a moment.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="activity-edit-page">
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:opacity-70" aria-label="Back" data-testid="edit-back-btn">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <span className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Edit activity</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="activity-edit-form">
          {/* Title */}
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              <FileText className="w-3.5 h-3.5" />
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 rounded-lg outline-none text-sm"
              style={{
                backgroundColor: 'var(--fy-surface-muted)',
                border: `1px solid ${errors.title ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                color: 'var(--fy-text-primary)',
              }}
              data-testid="edit-title-input"
              maxLength={200}
            />
            {errors.title && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>{errors.title}</p>}
          </Card>

          {/* Time */}
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              <Calendar className="w-3.5 h-3.5" />
              Start
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  backgroundColor: 'var(--fy-surface-muted)',
                  border: `1px solid ${errors.start_date ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                  color: 'var(--fy-text-primary)',
                }}
                data-testid="edit-start-date-input"
              />
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  backgroundColor: 'var(--fy-surface-muted)',
                  border: `1px solid ${errors.start_time ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                  color: 'var(--fy-text-primary)',
                }}
                data-testid="edit-start-time-input"
              />
            </div>
            {(errors.start_date || errors.start_time) && (
              <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>
                {errors.start_date || errors.start_time}
              </p>
            )}

            <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2 mt-4" style={{ color: 'var(--fy-text-disabled)' }}>
              <Clock className="w-3.5 h-3.5" />
              End (optional)
            </label>
            <div className="flex gap-2">
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  backgroundColor: 'var(--fy-surface-muted)',
                  border: '1px solid var(--fy-border-subtle)',
                  color: 'var(--fy-text-primary)',
                }}
                data-testid="edit-end-date-input"
              />
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg outline-none text-sm"
                style={{
                  backgroundColor: 'var(--fy-surface-muted)',
                  border: '1px solid var(--fy-border-subtle)',
                  color: 'var(--fy-text-primary)',
                }}
                data-testid="edit-end-time-input"
              />
            </div>
          </Card>

          {/* Location */}
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <label className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2" style={{ color: 'var(--fy-text-disabled)' }}>
              <MapPin className="w-3.5 h-3.5" />
              Location
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="w-full px-3 py-2 rounded-lg outline-none text-sm"
              style={{
                backgroundColor: 'var(--fy-surface-muted)',
                border: `1px solid ${errors.location ? 'var(--lk-terra)' : 'var(--fy-border-subtle)'}`,
                color: 'var(--fy-text-primary)',
              }}
              data-testid="edit-location-input"
              maxLength={300}
            />
            {errors.location && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>{errors.location}</p>}
          </Card>

          {/* Visibility */}
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--fy-text-disabled)' }}>
              Visibility
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'public', label: 'Public', desc: 'Visible to everyone — appears on calendar and map', icon: Globe },
                { value: 'private', label: 'Private', desc: 'Only visible to you and invited participants', icon: Lock },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => handleChange('visibility', opt.value)}
                  className="text-left p-3 rounded-lg transition-colors"
                  style={{
                    backgroundColor: form.visibility === opt.value ? 'var(--lk-sage-muted)' : 'var(--fy-surface-card)',
                    border: `2px solid ${form.visibility === opt.value ? 'var(--lk-sage)' : (errors.visibility ? 'var(--lk-terra)' : 'var(--fy-border-subtle)')}`,
                  }}
                  data-testid={`edit-visibility-${opt.value}`}
                >
                  <opt.icon className="w-4 h-4 mb-1" style={{ color: form.visibility === opt.value ? 'var(--lk-sage)' : 'var(--fy-text-disabled)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{opt.label}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>{opt.desc}</p>
                </button>
              ))}
            </div>
            {errors.visibility && <p className="text-xs mt-1" style={{ color: 'var(--lk-terra)' }}>{errors.visibility}</p>}
          </Card>

          {/* Save */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-full text-sm font-semibold"
            style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
            data-testid="edit-save-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </Button>
        </form>
      </main>
    </div>
  );
}
